import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useInfiniteJobs } from '~/domain/Jobs/useCases/useInfiniteJobs';
import { useUserSelector } from '~/hooks/useUserSelector';
import { WorkerClassificationTypeEnum } from '~/models';
import { getCandidateOfferId } from '../utils';

export const useCandidateJobs = (candidateId: string, officeId: string) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { workerClassification } = useUserSelector();

  const isContractor =
    workerClassification?.classificationType === WorkerClassificationTypeEnum.Contractor;
  const isEmployee =
    workerClassification?.classificationType === WorkerClassificationTypeEnum.Employee;

  const {
    jobs,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    isSuccess,
    ...queryProps
  } = useInfiniteJobs({
    professionalId: candidateId,
    officeId,
    status: 'pending-counter',
    enabled: !!candidateId && !!officeId,
  });

  // The backend truncates office-facing multipleCounters outside the unlimited-application
  // states, so a job can come back for this professional while their own counter is hidden.
  // Only list shifts whose counter is visible (GOTU-15661).
  const visibleJobs = useMemo(
    () => jobs.filter((job) => getCandidateOfferId(job, candidateId)),
    [jobs, candidateId],
  );

  const candidate = useMemo(() => {
    if (!visibleJobs.length) return null;

    const counter = visibleJobs[0].multipleCounters.find(
      ({ candidate }) => candidate?.id === candidateId,
    );

    if (!counter) return null;

    return {
      candidate: counter.candidate,
      hasWorkedWithDSO: counter.hasWorkedWithDSO || false,
      hasWorkedWithOffice: counter.hasWorkedWithOffice || false,
      backgroundCheckStatus: counter.requestor?.backgroundCheck?.status,
    };
  }, [visibleJobs, candidateId]);

  useEffect(() => {
    if (!visibleJobs.length && isSuccess) {
      navigate('/');
      queryClient.removeQueries({ queryKey: ['jobs-infinite'] });
    }
  }, [visibleJobs, isSuccess, navigate, queryClient]);

  return {
    candidate,
    jobs: visibleJobs,
    isContractor,
    isEmployee,
    isFirstLoading: isLoading,
    isGetJobsLoading: isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    ...queryProps,
  };
};
